import classNames from 'classnames';
import { isUndefined } from 'lodash';
import pluralize from 'pluralize';
import { memo } from 'react';

import { DefaultNoWorkspaceContentState } from '~/components/AllContent/DefaultNoWorkspaceContentState';
import { useCanShowBoardsInAllContent } from '~/components/AllContent/hooks/useCanShowBoardsInAllContent';
import { PrivateGalleryView, type PrivateGalleryViewProps } from '~/components/PrivateGallery/PrivateGalleryView';
import { PrivateBoardGalleryViewAsset } from '~/components/v2/components/board/gallery/PrivateBoardGalleryViewAsset';
import { PrivateBoardGalleryViewFile } from '~/components/v2/components/board/gallery/PrivateBoardGalleryViewFile';
import { SearchPageGalleryBoard } from '~/components/v2/components/search/SearchPageGalleryBoard';
import { GallerySectionHeader } from '~/components/v2/components/shared/gallery/GallerySectionHeader';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { useScrollElementContext } from '~/providers/ScrollElementProvider';

export const SearchPageGalleryView = memo(() => {
  const { scrollElementRef } = useScrollElementContext();
  const { canShowBoards } = useCanShowBoardsInAllContent();
  const { searchType } = usePrivateSearchContext();
  const { search } = useSearchParams();

  return (
    <PrivateGalleryView
      EmptyState={DefaultNoWorkspaceContentState}
      assetItemHorizontalSpacing={8}
      boardItemHorizontalSpacing={8}
      containerHorizontalPadding={16}
      rowVerticalSpacing={8}
      fileItemHorizontalSpacing={8}
      headerContainerHeight={32}
      renderAsset={renderAsset}
      renderBoard={renderBoard}
      renderFile={renderFile}
      renderHeader={renderHeader}
      scrollElementRef={scrollElementRef}
      showBoards={canShowBoards}
      includeDirectDescendantCount
      includeDirectDescendantBoardCount
      showAssetHeader={searchType !== 'smart' || isUndefined(search)}
    />
  );
});

SearchPageGalleryView.displayName = 'SearchPageGalleryView';

const renderBoard: Required<PrivateGalleryViewProps>['renderBoard'] = ({ box, data, index }) => {
  return <SearchPageGalleryBoard box={box} data={data} index={index} key={data.id} />;
};

const renderAsset: Required<PrivateGalleryViewProps>['renderAsset'] = ({ box, data, index }) => {
  return <PrivateBoardGalleryViewAsset box={box} data={data} index={index} key={data.id} />;
};

const renderFile: Required<PrivateGalleryViewProps>['renderFile'] = ({ box, data, index }) => {
  return <PrivateBoardGalleryViewFile box={box} data={data} index={index} key={data.id} />;
};

const renderHeader: Required<PrivateGalleryViewProps>['renderHeader'] = ({
  collapsed,
  onToggleCollapse,
  label,
  itemCount,
  isFirstSection,
}) => {
  return (
    <div className={!isFirstSection ? 'pt-6' : ''}>
      <GallerySectionHeader collapsed={collapsed} onToggleCollapse={onToggleCollapse} className={classNames('h-8')}>
        {itemCount?.toLocaleString()} {pluralize(label, itemCount)}
      </GallerySectionHeader>
    </div>
  );
};
